import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  PlayCircle,
  CheckCircle2,
  XCircle,
  Clock,
  SkipForward,
  AlertTriangle,
  ExternalLink,
  Terminal,
  Globe,
  Layers,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import {
  releasesApi,
  ReleaseStep,
  ReleaseStepExecution,
  ReleaseStepType,
  ChecklistItem,
} from "../api/client";
import { formatRelativeTime, titleCase } from "../lib/utils";

const stepTypeLabels: Record<ReleaseStepType, string> = {
  container_deploy: "Container Deploy",
  liquibase: "Liquibase Migration",
  workflow_model: "Workflow Model",
  sql_script: "SQL Script",
  config_change: "Config Change",
  manual_task: "Manual Task",
  curl_command: "cURL Command",
  file_operation: "File Operation",
  service_restart: "Service Restart",
  pre_deployment: "Pre-Deployment",
  post_deployment: "Post-Deployment",
};

const executionStatusConfig: Record<
  ReleaseStepExecution["status"],
  { label: string; icon: React.ReactNode; className: string }
> = {
  pending: {
    label: "Pending",
    icon: <Clock className="w-4 h-4" />,
    className: "bg-gray-100 text-gray-700",
  },
  in_progress: {
    label: "In Progress",
    icon: <PlayCircle className="w-4 h-4" />,
    className: "bg-yellow-100 text-yellow-700",
  },
  completed: {
    label: "Completed",
    icon: <CheckCircle2 className="w-4 h-4" />,
    className: "bg-green-100 text-green-700",
  },
  failed: {
    label: "Failed",
    icon: <XCircle className="w-4 h-4" />,
    className: "bg-red-100 text-red-700",
  },
  skipped: {
    label: "Skipped",
    icon: <SkipForward className="w-4 h-4" />,
    className: "bg-gray-100 text-gray-500",
  },
};

export default function ReleaseExecution() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const initialEnvId = searchParams.get("env");
  const [selectedEnvId, setSelectedEnvId] = useState<string | null>(
    initialEnvId,
  );
  const [expandedStepId, setExpandedStepId] = useState<string | null>(null);
  const [checklistResponses, setChecklistResponses] = useState<
    Record<string, boolean>
  >({});

  const { data: release, isLoading: releaseLoading } = useQuery({
    queryKey: ["release", id],
    queryFn: () => releasesApi.get(id!),
    enabled: !!id,
  });

  const { data: stepsData } = useQuery({
    queryKey: ["release-steps", id],
    queryFn: () => releasesApi.listSteps(id!),
    enabled: !!id,
  });

  const { data: environmentsData } = useQuery({
    queryKey: ["release-environments", id],
    queryFn: () => releasesApi.listEnvironments(id!),
    enabled: !!id,
  });

  const { data: executionsData, refetch: refetchExecutions } = useQuery({
    queryKey: ["release-executions", id, selectedEnvId],
    queryFn: () => releasesApi.listExecutions(id!, selectedEnvId!),
    enabled: !!id && !!selectedEnvId,
    refetchInterval: 5000, // Poll every 5 seconds
  });

  const steps = stepsData?.steps || [];
  const environments = environmentsData?.environments || [];
  const executions = executionsData?.executions || [];

  const selectedEnv = environments.find(
    (e) => e.environment_id === selectedEnvId,
  );

  // Auto-select first environment if none selected
  useEffect(() => {
    if (!selectedEnvId && environments.length > 0) {
      const firstPending = environments.find((e) => e.status === "pending");
      const firstInProgress = environments.find(
        (e) => e.status === "in_progress",
      );
      setSelectedEnvId(
        firstInProgress?.environment_id ||
          firstPending?.environment_id ||
          environments[0].environment_id,
      );
    }
  }, [environments, selectedEnvId]);

  const startEnvMutation = useMutation({
    mutationFn: () => releasesApi.startEnvironment(id!, selectedEnvId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["release-environments", id] });
      queryClient.invalidateQueries({
        queryKey: ["release-executions", id, selectedEnvId],
      });
    },
  });

  const executeStepMutation = useMutation({
    mutationFn: (stepId: string) =>
      releasesApi.executeStep(id!, selectedEnvId!, stepId),
    onSuccess: () => {
      refetchExecutions();
    },
  });

  const completeStepMutation = useMutation({
    mutationFn: ({
      stepId,
      status,
      output,
      error_message,
    }: {
      stepId: string;
      status: "completed" | "failed" | "skipped";
      output?: string;
      error_message?: string;
    }) =>
      releasesApi.completeStep(id!, selectedEnvId!, stepId, {
        status,
        output,
        error_message,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["release-environments", id] });
      refetchExecutions();
      setExpandedStepId(null);
    },
  });

  const updateChecklistMutation = useMutation({
    mutationFn: ({
      stepId,
      responses,
    }: {
      stepId: string;
      responses: Record<string, boolean>;
    }) => releasesApi.updateChecklist(id!, selectedEnvId!, stepId, responses),
    onSuccess: () => {
      refetchExecutions();
    },
  });

  const getExecutionForStep = (
    stepId: string,
  ): ReleaseStepExecution | undefined => {
    return executions.find((e) => e.release_step_id === stepId);
  };

  const getNextPendingStep = (): ReleaseStep | undefined => {
    const sortedSteps = [...steps].sort(
      (a, b) => a.display_order - b.display_order,
    );
    for (const step of sortedSteps) {
      const execution = getExecutionForStep(step.id);
      if (!execution || execution.status === "pending") {
        return step;
      }
    }
    return undefined;
  };

  const canStartEnvironment = selectedEnv?.status === "pending";
  const isEnvironmentStarted = selectedEnv?.status === "in_progress";
  const isEnvironmentCompleted =
    selectedEnv?.status === "completed" || selectedEnv?.status === "failed";

  const handleChecklistChange = (
    stepId: string,
    itemId: string,
    checked: boolean,
  ) => {
    const newResponses = {
      ...checklistResponses,
      [itemId]: checked,
    };
    setChecklistResponses(newResponses);
    updateChecklistMutation.mutate({ stepId, responses: newResponses });
  };

  if (releaseLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (!release) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900">
            Release not found
          </h2>
          <button
            onClick={() => navigate("/releases")}
            className="btn btn-primary mt-4"
          >
            Back to Releases
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/releases/${id}`)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Release
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {release.title}
            </h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
              <span>
                <strong>Service:</strong> {titleCase(release.service_name)}
              </span>
              <span>
                <strong>Version:</strong>{" "}
                <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                  {release.version}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Environment Selector Sidebar */}
        <div className="col-span-3">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Environments
            </h3>
            <div className="space-y-2">
              {environments.map((env) => {
                const statusConfig =
                  executionStatusConfig[
                    env.status as keyof typeof executionStatusConfig
                  ] || executionStatusConfig.pending;
                const isSelected = env.environment_id === selectedEnvId;
                return (
                  <button
                    key={env.id}
                    onClick={() => setSelectedEnvId(env.environment_id)}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                      isSelected
                        ? "border-brand-600 bg-brand-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">
                        {env.environment_name}
                      </span>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusConfig.className}`}
                      >
                        {statusConfig.icon}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {statusConfig.label}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="col-span-9">
          {selectedEnv && (
            <>
              {/* Environment Status Bar */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {selectedEnv.environment_name}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {selectedEnv.started_at
                        ? `Started ${formatRelativeTime(selectedEnv.started_at)}`
                        : "Not started"}
                      {selectedEnv.started_by_name &&
                        ` by ${selectedEnv.started_by_name}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {canStartEnvironment && (
                      <button
                        onClick={() => startEnvMutation.mutate()}
                        disabled={startEnvMutation.isPending}
                        className="btn btn-primary"
                      >
                        <PlayCircle className="w-4 h-4 mr-2" />
                        Start Deployment
                      </button>
                    )}
                    {isEnvironmentCompleted && (
                      <span
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${
                          selectedEnv.status === "completed"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {selectedEnv.status === "completed" ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : (
                          <XCircle className="w-5 h-5" />
                        )}
                        <span className="font-medium">
                          {selectedEnv.status === "completed"
                            ? "Completed"
                            : "Failed"}
                        </span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                {isEnvironmentStarted && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                      <span>Progress</span>
                      <span>
                        {
                          executions.filter(
                            (e) =>
                              e.status === "completed" ||
                              e.status === "skipped",
                          ).length
                        }{" "}
                        / {steps.length} steps
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-600 transition-all duration-300"
                        style={{
                          width: `${
                            (executions.filter(
                              (e) =>
                                e.status === "completed" ||
                                e.status === "skipped",
                            ).length /
                              steps.length) *
                            100
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Steps List */}
              {isEnvironmentStarted || isEnvironmentCompleted ? (
                <div className="space-y-4">
                  {steps
                    .sort((a, b) => a.display_order - b.display_order)
                    .map((step) => {
                      const execution = getExecutionForStep(step.id);
                      const status = execution?.status || "pending";
                      const statusConfig = executionStatusConfig[status];
                      const isExpanded = expandedStepId === step.id;
                      const isNextStep = getNextPendingStep()?.id === step.id;
                      const canExecute =
                        isEnvironmentStarted &&
                        status === "pending" &&
                        isNextStep;
                      const canComplete = status === "in_progress";
                      const checklist = step.checklist || [];

                      return (
                        <div
                          key={step.id}
                          className={`bg-white rounded-lg border-2 transition-colors ${
                            isNextStep && status === "pending"
                              ? "border-brand-600"
                              : "border-gray-200"
                          }`}
                        >
                          {/* Step Header */}
                          <div
                            className="p-4 flex items-center gap-4 cursor-pointer"
                            onClick={() =>
                              setExpandedStepId(isExpanded ? null : step.id)
                            }
                          >
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center ${statusConfig.className}`}
                            >
                              {statusConfig.icon}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">
                                  {step.display_order}. {step.title}
                                </span>
                                <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                                  {stepTypeLabels[step.step_type]}
                                </span>
                                {step.is_automated && (
                                  <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                                    Automated
                                  </span>
                                )}
                              </div>
                              {step.description && (
                                <p className="text-sm text-gray-500 mt-1">
                                  {step.description}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span
                                className={`px-3 py-1 rounded-full text-sm font-medium ${statusConfig.className}`}
                              >
                                {statusConfig.label}
                              </span>
                              {isExpanded ? (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </div>

                          {/* Expanded Content */}
                          {isExpanded && (
                            <div className="border-t border-gray-200 p-4">
                              {/* Step Config */}
                              {step.config &&
                                Object.keys(step.config).length > 0 && (
                                  <div className="mb-4">
                                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                                      Configuration
                                    </h4>
                                    <pre className="bg-gray-50 p-3 rounded-lg text-sm overflow-x-auto">
                                      {JSON.stringify(step.config, null, 2)}
                                    </pre>
                                  </div>
                                )}

                              {/* Checklist */}
                              {checklist.length > 0 && (
                                <div className="mb-4">
                                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                                    Checklist
                                  </h4>
                                  <div className="space-y-2">
                                    {checklist.map((item: ChecklistItem) => {
                                      const isChecked =
                                        execution?.checklist_responses?.[
                                          item.id
                                        ] ||
                                        checklistResponses[item.id] ||
                                        false;
                                      return (
                                        <label
                                          key={item.id}
                                          className="flex items-center gap-3 p-2 rounded hover:bg-gray-50"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={(e) =>
                                              handleChecklistChange(
                                                step.id,
                                                item.id,
                                                e.target.checked,
                                              )
                                            }
                                            disabled={
                                              status === "completed" ||
                                              status === "skipped"
                                            }
                                            className="w-5 h-5 rounded border-gray-300 text-brand-600"
                                          />
                                          <span
                                            className={`text-sm ${
                                              isChecked
                                                ? "text-gray-500 line-through"
                                                : "text-gray-700"
                                            }`}
                                          >
                                            {item.text}
                                            {item.required && (
                                              <span className="text-red-500 ml-1">
                                                *
                                              </span>
                                            )}
                                          </span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Execution Output */}
                              {execution?.output && (
                                <div className="mb-4">
                                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                    <Terminal className="w-4 h-4" />
                                    Output
                                  </h4>
                                  <pre className="bg-gray-900 text-gray-100 p-3 rounded-lg text-sm overflow-x-auto">
                                    {execution.output}
                                  </pre>
                                </div>
                              )}

                              {/* Error Message */}
                              {execution?.error_message && (
                                <div className="mb-4">
                                  <h4 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4" />
                                    Error
                                  </h4>
                                  <pre className="bg-red-50 text-red-700 p-3 rounded-lg text-sm overflow-x-auto">
                                    {execution.error_message}
                                  </pre>
                                </div>
                              )}

                              {/* Jenkins Build Link */}
                              {execution?.jenkins_build_url && (
                                <div className="mb-4">
                                  <a
                                    href={execution.jenkins_build_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-brand-600 hover:text-brand-700"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                    View Jenkins Build
                                  </a>
                                </div>
                              )}

                              {/* Action Buttons */}
                              <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                                {canExecute && (
                                  <button
                                    onClick={() =>
                                      executeStepMutation.mutate(step.id)
                                    }
                                    disabled={executeStepMutation.isPending}
                                    className="btn btn-primary"
                                  >
                                    <PlayCircle className="w-4 h-4 mr-2" />
                                    Start Step
                                  </button>
                                )}

                                {canComplete && (
                                  <>
                                    <button
                                      onClick={() =>
                                        completeStepMutation.mutate({
                                          stepId: step.id,
                                          status: "completed",
                                        })
                                      }
                                      disabled={completeStepMutation.isPending}
                                      className="btn btn-primary"
                                    >
                                      <CheckCircle2 className="w-4 h-4 mr-2" />
                                      Mark Complete
                                    </button>
                                    <button
                                      onClick={() =>
                                        completeStepMutation.mutate({
                                          stepId: step.id,
                                          status: "failed",
                                          error_message:
                                            "Manually marked as failed",
                                        })
                                      }
                                      disabled={completeStepMutation.isPending}
                                      className="btn btn-secondary text-red-600"
                                    >
                                      <XCircle className="w-4 h-4 mr-2" />
                                      Mark Failed
                                    </button>
                                    <button
                                      onClick={() =>
                                        completeStepMutation.mutate({
                                          stepId: step.id,
                                          status: "skipped",
                                        })
                                      }
                                      disabled={completeStepMutation.isPending}
                                      className="btn btn-secondary"
                                    >
                                      <SkipForward className="w-4 h-4 mr-2" />
                                      Skip
                                    </button>
                                  </>
                                )}

                                {execution?.completed_at && (
                                  <span className="text-sm text-gray-500">
                                    Completed{" "}
                                    {formatRelativeTime(execution.completed_at)}
                                    {execution.executed_by_name &&
                                      ` by ${execution.executed_by_name}`}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                  <Layers className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Ready to Deploy
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Start the deployment to execute steps for this environment
                  </p>
                  <button
                    onClick={() => startEnvMutation.mutate()}
                    disabled={startEnvMutation.isPending}
                    className="btn btn-primary"
                  >
                    <PlayCircle className="w-4 h-4 mr-2" />
                    Start Deployment
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
